import type { ReactNode } from 'react';
import { AlertTriangle, CheckCircle2, Info, XCircle } from 'lucide-react';
import { cn } from '@/lib/utils';

export type AlertTone = 'info' | 'success' | 'warning' | 'danger';

const TONE_STYLES: Record<AlertTone, string> = {
  info: 'border-teal-ink bg-white text-ink',
  success: 'border-success bg-white text-ink',
  warning: 'border-warning bg-white text-ink',
  danger: 'border-danger bg-danger-soft text-ink'
};

const TONE_ICONS: Record<AlertTone, typeof Info> = {
  info: Info,
  success: CheckCircle2,
  warning: AlertTriangle,
  danger: XCircle
};

const TONE_ICON_COLORS: Record<AlertTone, string> = {
  info: 'text-teal-ink',
  success: 'text-success',
  warning: 'text-warning',
  danger: 'text-danger'
};

export interface AlertProps {
  tone?: AlertTone;
  title?: string;
  children?: ReactNode;
  className?: string;
}

export const Alert = ({ tone = 'info', title, children, className }: AlertProps) => {
  const Icon = TONE_ICONS[tone];

  return (
    <div
      role={tone === 'danger' ? 'alert' : 'status'}
      className={cn('flex items-start gap-3 rounded-md border-l-4 border-2 p-4', TONE_STYLES[tone], className)}
    >
      <Icon className={cn('mt-0.5 size-6 shrink-0', TONE_ICON_COLORS[tone])} aria-hidden="true" />
      <div className="flex flex-col gap-1">
        {title && <p className="font-display text-lg font-bold text-navy">{title}</p>}
        {children && <div className="text-base">{children}</div>}
      </div>
    </div>
  );
};
